import { useEffect, useId, useSyncExternalStore } from "react";
import { chooseVoice, findClip, forVoice, speakable, type NarrationManifest } from "@buildlab/shared/narration";
import { Speaker } from "./icons";
import "./Narrate.css";

// Narration: BuildLab reads directions aloud (decision 90, D48).
//
//   <Narrate text={step.text} clip={`${assignment.id}/${step.id}`} />   fixed text: plays the recorded
//       clip from /narration/ when one was recorded for exactly this text, else the device voice
//   <Narrate text={reply} source="reply" />   helper replies: the device's own voice only (cleaned
//       of code and Markdown, first ~600 characters); reply text never leaves the Chromebook
//   <AutoRead text={…} clip={…} />   reads the text when it changes, if "Read steps to me" is on
//   <ReadStepsToggle />   the setting (off by default; beside Large text)
//
// One thing speaks at a time: starting any narration stops the one before. Browsers only allow
// sound after the student has clicked or typed on the page, so auto-read waits for that.

// ---- Recorded clips ---------------------------------------------------------------------------

let manifest: Promise<NarrationManifest | null> | null = null;
function loadManifest(): Promise<NarrationManifest | null> {
  manifest ??= fetch("/narration/manifest.json")
    .then((r) => (r.ok ? (r.json() as Promise<NarrationManifest>) : null))
    .catch(() => null);
  return manifest;
}

// ---- Who is speaking --------------------------------------------------------------------------

let playingId: string | null = null;
let stopCurrent: (() => void) | null = null;
let run = 0; // bumps on every start/stop, so a slow manifest or voice load can't start stale speech
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** Stops whatever narration is playing. */
export function stopNarration() {
  run++;
  const stop = stopCurrent;
  stopCurrent = null;
  try {
    stop?.();
  } catch {}
  if (playingId !== null) {
    playingId = null;
    notify();
  }
}

/** True while the narration started with this id is playing. */
export function useNarrating(id: string): boolean {
  return useSyncExternalStore(subscribe, () => playingId === id, () => false);
}

export interface NarrateOptions {
  /** Manifest key of the recorded clip, like `${assignmentId}/${stepId}`. */
  clip?: string;
  /** "reply": a helper reply, device voice only, cleaned and shortened. Default "fixed". */
  source?: "fixed" | "reply";
  /** Which button (or auto-reader) owns this, for its playing state. */
  id?: string;
}

/** Says `text`: the recorded clip if there is one, else the device's own voice. Never throws. */
export async function narrate(text: string, { clip, source = "fixed", id = "narrate" }: NarrateOptions = {}): Promise<void> {
  stopNarration();
  const mine = ++run;
  playingId = id;
  notify();
  const done = () => {
    if (run !== mine) return;
    stopCurrent = null;
    playingId = null;
    notify();
  };
  try {
    if (source === "fixed") {
      const file = findClip(await loadManifest(), text, clip);
      if (run !== mine) return;
      if (file && (await playClip(`/narration/${file}`, mine, done))) return;
      if (run !== mine) return;
    }
    const words = source === "reply" ? speakable(text).text : speakable(forVoice(text), Infinity).text;
    if (!(await speak(words, mine, done))) done();
  } catch {
    done();
  }
}

/** Plays a recorded clip; false if it can't (then the device voice reads the text instead). */
async function playClip(src: string, mine: number, done: () => void): Promise<boolean> {
  if (typeof Audio === "undefined") return false;
  const audio = new Audio(src);
  stopCurrent = () => {
    audio.pause();
    audio.src = "";
  };
  audio.onended = done;
  try {
    await audio.play();
    return true;
  } catch {
    if (run === mine) stopCurrent = null;
    return false;
  }
}

// ---- The device's own voice (Web Speech API, on the Chromebook) -----------------------------

function synth(): SpeechSynthesis | null {
  return typeof window !== "undefined" && "speechSynthesis" in window && typeof SpeechSynthesisUtterance !== "undefined" ? window.speechSynthesis : null;
}

/** Voices load asynchronously in Chrome: wait for them briefly. */
function voices(s: SpeechSynthesis): Promise<SpeechSynthesisVoice[]> {
  const now = s.getVoices();
  if (now.length) return Promise.resolve(now);
  return new Promise((resolve) => {
    const finish = () => {
      s.removeEventListener?.("voiceschanged", finish);
      clearTimeout(timer);
      resolve(s.getVoices());
    };
    const timer = setTimeout(finish, 1500);
    s.addEventListener?.("voiceschanged", finish);
  });
}

async function speak(text: string, mine: number, done: () => void): Promise<boolean> {
  const s = synth();
  if (!s || !text) return false;
  const voice = chooseVoice(await voices(s));
  if (run !== mine) return true;
  if (!voice) return false; // no on-device English voice (never fall back to a network one)
  s.cancel();
  // One utterance per sentence: Chrome cuts long utterances off, and stopping is quicker.
  const parts = text.match(/[^.!?…]+[.!?…]*\s*/g)?.map((p) => p.trim()).filter(Boolean) ?? [text];
  stopCurrent = () => s.cancel();
  parts.forEach((part, i) => {
    const u = new SpeechSynthesisUtterance(part);
    u.voice = voice;
    u.lang = voice.lang;
    u.rate = 0.95;
    if (i === parts.length - 1) {
      u.onend = done;
      u.onerror = done;
    }
    s.speak(u);
  });
  return true;
}

// ---- The speaker button -----------------------------------------------------------------------

export function Narrate({ text, clip, source = "fixed", label, className = "" }: { text: string; clip?: string; source?: "fixed" | "reply"; label?: string; className?: string }) {
  const id = useId();
  const playing = useNarrating(id);
  useEffect(() => () => {
    if (playingId === id) stopNarration();
  }, [id]);
  if (!text.trim()) return null;
  const name = playing ? "Stop reading" : (label ?? (source === "reply" ? "Read the helper's reply aloud" : "Read this aloud"));
  return (
    <button
      type="button"
      className={`narrate${playing ? " playing" : ""} ${className}`.trim()}
      aria-label={name}
      aria-pressed={playing}
      title={name}
      onClick={() => (playing ? stopNarration() : void narrate(text, { clip, source, id }))}
    >
      <Speaker size={16} waves={!playing} />
      {playing && <span className="narrate-bars" aria-hidden="true"><i /><i /><i /></span>}
    </button>
  );
}

// ---- "Read steps to me" -------------------------------------------------------------------------

const READ_KEY = "buildlab-read-steps";
const readListeners = new Set<() => void>();
function readStepsOn(): boolean {
  try {
    return localStorage.getItem(READ_KEY) === "1";
  } catch {
    return false;
  }
}
export function setReadSteps(on: boolean) {
  try {
    localStorage.setItem(READ_KEY, on ? "1" : "0");
  } catch {}
  if (!on) stopNarration();
  readListeners.forEach((l) => l());
}
export function useReadSteps(): boolean {
  return useSyncExternalStore(
    (l) => {
      readListeners.add(l);
      return () => readListeners.delete(l);
    },
    readStepsOn,
    () => false,
  );
}

/** The setting, styled like the Large text toggle beside it. Off by default (Q11: headphones). */
export function ReadStepsToggle() {
  const on = useReadSteps();
  return (
    <label className="toggle" title="Read steps to me: BuildLab reads each mission step aloud">
      <input id="read-steps" type="checkbox" checked={on} aria-label="Read steps to me" onChange={(e) => setReadSteps(e.target.checked)} />
      <span className="bar-icon">
        <Speaker size={18} />
      </span>
      <span className="bar-label">Read steps to me</span>
    </label>
  );
}

/** Reads `text` aloud whenever it changes while "Read steps to me" is on (after the first click or key). */
export function AutoRead({ text, clip }: { text: string; clip?: string }) {
  const on = useReadSteps();
  useEffect(() => {
    if (!on || !text.trim()) return;
    const go = () => void narrate(text, { clip, id: "auto-read" });
    const active = typeof navigator !== "undefined" && (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation;
    if (!active || active.hasBeenActive) {
      go();
    } else {
      // No click or key yet, so the browser would block sound: start on the first one.
      const first = () => {
        window.removeEventListener("pointerdown", first, true);
        window.removeEventListener("keydown", first, true);
        go();
      };
      window.addEventListener("pointerdown", first, true);
      window.addEventListener("keydown", first, true);
      return () => {
        window.removeEventListener("pointerdown", first, true);
        window.removeEventListener("keydown", first, true);
        if (playingId === "auto-read") stopNarration();
      };
    }
    return () => {
      if (playingId === "auto-read") stopNarration();
    };
  }, [on, text, clip]);
  return null;
}
